import cryptoRandomString from "crypto-random-string";
import moment from "moment";
import queryString from "query-string";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import generalOptionSets from "../../options/general.json";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleAddFieldTableRow,
  handleDeleteFieldTableRow,
  handleFieldTableChange,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldText,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  FieldTable,
  FormCheckbox,
  FormInput,
  FormSelect,
} from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import {
  getFormsySubmissionsByFormIdForClient,
  getSignedEncounters,
} from "../services/soar";
import { getSchema } from "./joi/validation";

function SCFRCPSFamilyServicePlan() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow, unshift = false) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow, unshift);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
      setCommentToEdit(undefined);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [controllerSignal, setControllerSignal] = useState();
  const [encounters, setEncounters] = useState([]);
  const [loadStatus, setLoadStatus] = useState({});
  const [fastAssessments, setFASTAssessments] = useState([]);
  const [latestCaregiverOptions, setLatestCaregiverOptions] = useState([]);
  const [latestChildOptions, setLatestChildOptions] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    setControllerSignal(controller.signal);
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (controllerSignal) {
      const load = async () => {
        setLoadStatus((ls) => {
          return { ...ls, loadingFastAssessments: true };
        });
        const response = await getFormsySubmissionsByFormIdForClient(
          "B28A01B4-58B5-EE11-AD37-DC98408D5418", // SC FRSCP Fast Assessment Form Container Id
          clientid,
          controllerSignal
        );

        if (!controllerSignal.aborted) {
          if (response) {
            const result = JSON.parse(response);

            if (result?.FormSubmissions?.Submission?.length > 0) {
              setFASTAssessments(
                result.FormSubmissions.Submission.sort((a, b) => {
                  const aDate = moment(
                    getFieldValue(a.FormResults.results.DateCompleted)
                  );
                  const bDate = moment(
                    getFieldValue(b.FormResults.results.DateCompleted)
                  );

                  return aDate.isBefore(bDate)
                    ? 1
                    : bDate.isBefore(aDate)
                    ? -1
                    : 0;
                })
              );
            }
          }

          setLoadStatus((ls) => {
            return { ...ls, loadingFastAssessments: false };
          });
        }
      };

      load();
    }
  }, [clientid, controllerSignal]);

  useEffect(() => {
    if (!formDisabled && !loadStatus.encountersLoaded && controllerSignal) {
      const loadEncounters = async () => {
        setLoadStatus((ls) => {
          return { ...ls, loadingEncounters: true };
        });
        const response = await getSignedEncounters(clientid, controllerSignal);

        if (!controllerSignal.aborted) {
          setEncounters(response || []);
          setLoadStatus((ls) => {
            return { ...ls, loadingEncounters: false, encountersLoaded: true };
          });
        }
      };

      loadEncounters();
    }
  }, [clientid, formDisabled, loadStatus.encountersLoaded, controllerSignal]);

  const convertEncounters = useCallback(() => {
    setData((d) => {
      const obj = { ...d };
      const selectedProgram = getFieldValue(obj.SelectedProgram)
        ? getFieldValue(obj.SelectedProgram)
        : null;
      const startDate = getFieldValue(obj.StartDate)
        ? moment(getFieldValue(obj.StartDate))
        : null;
      const endDate = getFieldValue(obj.EndDate)
        ? moment(getFieldValue(obj.EndDate))
        : null;

      console.group("Process Records");
      console.log("Selected Program: ", selectedProgram);
      console.log("Start Date: ", startDate);
      console.log("End Date: ", endDate);
      console.log("Encounters: ", encounters);
      console.groupEnd();

      if (!selectedProgram || !startDate || !endDate) {
        return obj;
      }

      const monthlyEncounters = encounters.filter((encounter) => {
        if (getFieldName(encounter.ccx_deliverymethod) !== "Face-to-Face") {
          return false;
        }

        const encounterStartDate = moment(
          getFieldText(encounter.ccx_starttime)
        ).startOf("day");

        return (
          getFieldText(encounter.ccx_clientprogram) ===
            getFieldValue(obj.SelectedProgram) &&
          encounterStartDate.isSameOrAfter(startDate) &&
          encounterStartDate.isSameOrBefore(endDate)
        );
      });

      if (monthlyEncounters.length > 0) {
        console.log("Monthly Encounters: ", monthlyEncounters);
        const existingContactDetails = JSON.parse(
          getFieldValue(obj.ContactDetails) || "[]"
        );

        const existingRecordIds = existingContactDetails.map(
          (service) => service.RecordId
        );

        const serviceDetails = monthlyEncounters
          .filter(
            (encounter) =>
              !existingRecordIds.includes(encounter.ccx_encounterid)
          )
          .map((encounter) => {
            const startTime = moment(getFieldText(encounter.ccx_starttime));
            const endTime = moment(getFieldText(encounter.ccx_endtime));

            return {
              IsEditable: true,
              RecordId: encounter.ccx_encounterid,
              VisitDate: startTime.format(moment.HTML5_FMT.DATE),
              VisitDuration: endTime.diff(startTime, "minutes"),
              FullStartDate: startTime,
              FullEndDate: endTime,
            };
          });

        let allContactDetails = existingContactDetails
          .concat(serviceDetails)
          .sort((a, b) => {
            if (!a.FullStartDate) {
              return 1;
            } else if (!b.FullStartDate) {
              return -1;
            }

            const aStart = moment(a.FullStartDate);
            const bStart = moment(b.FullStartDate);

            return aStart.isBefore(bStart)
              ? -1
              : bStart.isBefore(aStart)
              ? 1
              : 0;
          });

        if (allContactDetails.count === 0)
          allContactDetails = [{ IsEditable: true }];

        console.log("Service Details: ", allContactDetails);
        obj.ContactDetails["@value"] = JSON.stringify(allContactDetails);
      }

      return obj;
    });
  }, [encounters]);

  const getFamilyNeeds = (assessments) => {
    const familyDomainFieldNames = [
      "FamilyConflict",
      "FamilyRoleAppropriateness",
      "FamilySafety",
      "FinancialResources",
      "ResidentialStability",
      "FamilySocialIdentity",
      "FamilyCollaboration",
      "SiblingRelationships",
      "ExtendedRelationships",
      "NaturalSupports",
      "FamilyTraditions",
      "KnowledgeOfNeeds",
      "KnowledgeOfOptions",
      "KnowledgeOfRights",
      "AbilityToListen",
      "AbilityToCommunicate",
      "FamilyLanguage",
      "FamilyCulturalStress",
    ];

    const familyDomainFieldLabels = [
      "Family Conflict",
      "Family Role Appropriateness",
      "Family Safety",
      "Financial Resources",
      "Residential Stability",
      "Family Social Identity",
      "Parental-Caregiver Collaboration",
      "Relations Among Siblings",
      "Extended Family Relations",
      "Natural Supports",
      "Traditions and Cultural Rituals",
      "Knowledge of Family-Child Needs",
      "Knowledge of Service Options",
      "Knowledge of Rights and Responsibilities",
      "Ability to Listen",
      "Ability to Communicate",
      "Language",
      "Cultural Stress",
    ];

    setData((d) => {
      const obj = { ...d };
      const programAssessments = assessments.filter(
        (assessment) =>
          `{${assessment.ClientProgramId}}` ===
          getFieldValue(obj.SelectedProgram)
      );

      const initialAssessment = programAssessments.filter(
        (assessment) =>
          getFieldValue(assessment.FormResults.results.AssessmentType) ===
          "Initial"
      )[0];

      const latestAssessment = programAssessments.filter(
        (assessment) =>
          getFieldValue(assessment.FormResults.results.AssessmentType) !==
            "Initial" &&
          assessment.SubmissionId !== initialAssessment?.SubmissionId
      )[0];

      const initialFamilyNeeds = [];
      const latestFamilyNeeds = [];

      if (initialAssessment) {
        const formResults = initialAssessment.FormResults.results;

        familyDomainFieldNames.forEach((fieldName, index) => {
          const fieldValue = parseInt(getFieldValue(formResults[fieldName]));

          if (fieldValue >= 2) {
            initialFamilyNeeds.push(
              `${familyDomainFieldLabels[index]} (${fieldValue})`
            );
          }
        });
      }

      let latestAssessmentType;

      if (latestAssessment) {
        const formResults = latestAssessment.FormResults.results;
        latestAssessmentType = getFieldValue(formResults.AssessmentType);

        familyDomainFieldNames.forEach((fieldName, index) => {
          const fieldValue = parseInt(getFieldValue(formResults[fieldName]));

          if (fieldValue >= 2) {
            latestFamilyNeeds.push(
              `${familyDomainFieldLabels[index]} (${fieldValue})`
            );
          }
        });
      }

      let familyUnitNeeds = "";

      if (initialFamilyNeeds.length > 0) {
        familyUnitNeeds = `Initial Assessment: ${initialFamilyNeeds.join(
          ", "
        )}${
          latestFamilyNeeds.length > 0
            ? `

`
            : ""
        }`;
      }

      if (latestFamilyNeeds.length > 0) {
        familyUnitNeeds = `${
          familyUnitNeeds || ""
        }Latest Assessment (${latestAssessmentType}): ${latestFamilyNeeds.join(
          ", "
        )}`;
      }

      obj.FamilyUnitNeeds["@value"] = familyUnitNeeds;
      return obj;
    });
  };

  const getCaregiverNeeds = (assessments) => {
    const caregiverDomainFieldNames = [
      "Monitoring",
      "Knowledge",
      "Boundaries",
      "Discipline",
      "Medical",
      "MentalHealth",
      "Developmental",
      "SubstanceUse",
      "TraumaticReaction",
      "Psychosis",
      "MoodDisturbance",
      "Depression",
      "ImpulseControl",
      "Anxiety",
      "Problems",
      "Antisocial",
      "Anger",
      "EatingDisturbance",
      "SUSeverity",
      "SUDuration",
      "RecoveryStage",
      "PeerInfluences",
      "EnvInfluences",
      "RecoverySupport",
      "AcuteIntoxication",
      "WithdrawalHistory",
      "WithdrawalRisks",
      "TriggerAwareness",
      "SexualAbuse",
      "PhysicalAbuse",
      "Neglect",
      "EmotionalAbuse",
      "MedicalTrauma",
      "DisasterTrauma",
      "FamilyViolence",
      "SchoolViolence",
      "WarTrauma",
      "CriminalActsTrauma",
      "ParentalCriminalTrauma",
      "AttachmentTrauma",
    ];

    const caregiverDomainFieldLabels = [
      "Monitoring",
      "Knowledge",
      "Boundaries",
      "Discipline",
      "Medical/Physical",
      "Mental Health",
      "Developmental",
      "Substance Use",
      "Posttraumatic Reactions",
      "Psychosis (Thought Disorder)",
      "Mood Disturbance",
      "Depression",
      "Impulse Control",
      "Anxiety",
      "Interpersonal Problems",
      "Antisocial Behavior",
      "Anger Control",
      "Eating Disturbances",
      "Severity of Use",
      "Duration of Use",
      "Stage of Recovery",
      "Peer Influences",
      "Environmental Influences",
      "Recovery Support in Community",
      "Acute Intoxication",
      "Withdrawal History",
      "Withdrawal Risks",
      "Awareness of Relapse Triggers",
      "Sexual Abuse",
      "Physical Abuse",
      "Neglect",
      "Emotional Abuse",
      "Medical Trauma",
      "Natural or Manmade Disaster",
      "Witness to Family Violence",
      "Witness to Comm./School Violence",
      "War/Terrorism Affected",
      "Witness/Victim of Criminal Acts",
      "Parental Criminal Behavior",
      "Disrupt. in Caregiving/Attachment",
    ];

    setData((d) => {
      const obj = { ...d };
      const programAssessments = assessments.filter(
        (assessment) =>
          `{${assessment.ClientProgramId}}` ===
          getFieldValue(obj.SelectedProgram)
      );

      const initialAssessment = programAssessments.filter(
        (assessment) =>
          getFieldValue(assessment.FormResults.results.AssessmentType) ===
          "Initial"
      )[0];

      const caregivers = [];

      if (initialAssessment) {
        const formResults = initialAssessment.FormResults.results;
        const caregiverAssessments = JSON.parse(
          getFieldValue(formResults.CaregiverAssessments)
        );

        caregiverAssessments.forEach((assessment) => {
          const caregiver = {
            CaregiverName: assessment.Name,
            Needs: "",
            Goals: [
              {
                Objectives: [
                  {
                    ProgressDetails: [
                      { Date: moment(formResults.DateCompleted).format("L") },
                    ],
                  },
                ],
              },
            ],
          };
          const initialCaregiverNeeds = [];
          caregiverDomainFieldNames.forEach((fieldName, index) => {
            const fieldValue = parseInt(assessment[fieldName]);

            if (fieldValue >= 2) {
              initialCaregiverNeeds.push(
                `${caregiverDomainFieldLabels[index]} (${fieldValue})`
              );
            }
          });
          caregiver.Needs = `Initial Assessment: ${initialCaregiverNeeds.join(
            ", "
          )}`;
          caregivers.push(caregiver);
        });
      }

      const latestAssessment = programAssessments.filter(
        (assessment) =>
          getFieldValue(assessment.FormResults.results.AssessmentType) !==
            "Initial" &&
          assessment.SubmissionId !== initialAssessment?.SubmissionId
      )[0];

      if (latestAssessment) {
        const formResults = latestAssessment.FormResults.results;
        const caregiverAssessments = JSON.parse(
          getFieldValue(formResults.CaregiverAssessments)
        );
        const latestAssessmentType = getFieldValue(formResults.AssessmentType);

        caregiverAssessments.forEach((assessment) => {
          const caregiver = { _id: assessment._id, name: assessment.Name };
          const latestCaregiverNeeds = [];
          caregiverDomainFieldNames.forEach((fieldName, index) => {
            const fieldValue = parseInt(assessment[fieldName]);

            if (fieldValue >= 2) {
              latestCaregiverNeeds.push(
                `${caregiverDomainFieldLabels[index]} (${fieldValue})`
              );
            }
          });
          caregiver.Needs = `

Latest Assessment (${latestAssessmentType}): ${latestCaregiverNeeds.join(
            ", "
          )}`;
          setLatestCaregiverOptions((o) => [...o, caregiver]);
        });
      }

      obj.CaregiverGoals["@value"] = JSON.stringify(caregivers);
      return obj;
    });
  };

  const getChildNeeds = (assessments) => {
    const toddlerDomainFieldNames = [
      "EarlyEducation",
      "SocialFunctioning",
      "Developmental",
      "Medical",
      "FamilyStrengths",
      "Interpersonal",
      "Playfulness",
      "Adaptability",
    ];

    const toddlerDomainFieldLabels = [
      "Early Education",
      "Social and Emotional Functioning",
      "Developmental/Intellectual",
      "Medical/Physical",
      "Family Strengths",
      "Interpersonal",
      "Playfulness",
      "Persistence & Adaptability",
    ];

    const childDomainFieldNames = [
      "SocialFuntioning",
      "Medical",
      "Sleep",
      "Developmental",
      "SexualDevelopment",
      "School",
      "Behavioral",
      "RiskBehaviors",
      "Adjustment",
      "Psychosis",
      "Impulsivity",
      "Depression",
      "Anxiety",
      "Oppositional",
      "Conduct",
      "SubstanceUse",
      "EatingDisturbance",
      "Anger",
      "SuicideRisk",
      "SelfInjurious",
      "Recklessness",
      "DangerToOthers",
      "SexualAggression",
      "Runaway",
      "Delinquent",
      "FireSetting",
      "Misbehavior",
      "Bullying",
      "Exploitation",
      "SexualAbuse",
      "PhysicalAbuse",
      "Neglect",
      "EmotionalAbuse",
      "MedicalTrauma",
      "DisasterTrauma",
      "FamilyViolence",
      "SchoolViolence",
      "WarTrauma",
      "CriminalActsTrauma",
      "ParentalCriminalTrauma",
      "AttachmentTrauma",
      "FamilyStrengths",
      "CommunityLife",
      "Talents",
      "Optimism",
    ];

    const childDomainFieldLabels = [
      "Social Functioning",
      "Medical/Physical",
      "Sleep",
      "Developmental/Intellectual",
      "Sexual Development",
      "School",
      "Behavioral/Mental Health Needs",
      "Risk Behaviors",
      "Adjustment to Trauma",
      "Psychosis (Thought Disorder)",
      "Impulsivity/Hyperactivity",
      "Depression",
      "Anxiety",
      "Oppositional",
      "Conduct",
      "Substance Use",
      "Eating Disturbances",
      "Anger Control",
      "Suicide Risk",
      "Non-Suicidal Self-Injurious Behavior",
      "Other Self-Harm (Recklessness)",
      "Danger to Others",
      "Sexual Aggression",
      "Runaway",
      "Delinquent Behavior",
      "Fire Setting",
      "Intentional Misbehavior",
      "Bullying",
      "Victimization/Exploitation",
      "Sexual Abuse",
      "Physical Abuse",
      "Neglect",
      "Emotional Abuse",
      "Medical Trauma",
      "Natural or Manmade Disaster",
      "Witness to Family Violence",
      "Witness to Comm./School Violence",
      "War/Terrorism Affected",
      "Witness/Victim of Criminal Acts",
      "Parental Criminal Behavior",
      "Disrupt. in Caregiving/Attachment",
      "Family Strengths",
      "Community Life",
      "Talents and Interests",
      "Optimism",
    ];

    setData((d) => {
      const obj = { ...d };
      const programAssessments = assessments.filter(
        (assessment) =>
          `{${assessment.ClientProgramId}}` ===
          getFieldValue(obj.SelectedProgram)
      );

      const initialAssessment = programAssessments.filter(
        (assessment) =>
          getFieldValue(assessment.FormResults.results.AssessmentType) ===
          "Initial"
      )[0];

      const toddlers = [];
      const children = [];

      if (initialAssessment) {
        const formResults = initialAssessment.FormResults.results;

        if (getFieldValue(formResults.ToddlerAssessmentsNA) !== "true") {
          const toddlerAssessments = JSON.parse(
            getFieldValue(formResults.ToddlerAssessments)
          );
          toddlerAssessments.forEach((assessment) => {
            const toddler = {
              ChildName: assessment.Name,
              Needs: "",
              Goals: [
                {
                  Objectives: [
                    {
                      ProgressDetails: [
                        { Date: moment(formResults.DateCompleted).format("L") },
                      ],
                    },
                  ],
                },
              ],
            };
            const initialToddlerNeeds = [];
            toddlerDomainFieldNames.forEach((fieldName, index) => {
              const fieldValue = parseInt(assessment[fieldName]);

              if (fieldValue >= 2) {
                initialToddlerNeeds.push(
                  `${toddlerDomainFieldLabels[index]} (${fieldValue})`
                );
              }
            });
            toddler.Needs = `Initial Assessment: ${initialToddlerNeeds.join(
              ", "
            )}`;
            toddlers.push(toddler);
          });
        }

        if (getFieldValue(formResults.ChildAssessmentsNA) !== "true") {
          const childAssessments = JSON.parse(
            getFieldValue(formResults.ChildAssessments)
          );
          childAssessments.forEach((assessment) => {
            const child = {
              ChildName: assessment.Name,
              Needs: "",
              Goals: [
                {
                  Objectives: [
                    {
                      ProgressDetails: [
                        { Date: moment(formResults.DateCompleted).format("L") },
                      ],
                    },
                  ],
                },
              ],
            };
            const initialChildNeeds = [];
            childDomainFieldNames.forEach((fieldName, index) => {
              const fieldValue = parseInt(assessment[fieldName]);

              if (fieldValue >= 2) {
                initialChildNeeds.push(
                  `${childDomainFieldLabels[index]} (${fieldValue})`
                );
              }
            });
            child.Needs = `Initial Assessment: ${initialChildNeeds.join(", ")}`;
            children.push(child);
          });
        }
      }

      const latestAssessment = programAssessments.filter(
        (assessment) =>
          getFieldValue(assessment.FormResults.results.AssessmentType) !==
            "Initial" &&
          assessment.SubmissionId !== initialAssessment?.SubmissionId
      )[0];

      if (latestAssessment) {
        const formResults = latestAssessment.FormResults.results;

        if (getFieldValue(formResults.ToddlerAssessmentsNA) !== "true") {
          const toddlerAssessments = JSON.parse(
            getFieldValue(formResults.ToddlerAssessments)
          );
          const latestAssessmentType = getFieldValue(
            formResults.AssessmentType
          );

          toddlerAssessments.forEach((assessment) => {
            const toddler = { _id: assessment._id, name: assessment.Name };

            const latestChildNeeds = [];
            childDomainFieldNames.forEach((fieldName, index) => {
              const fieldValue = parseInt(assessment[fieldName]);

              if (fieldValue >= 2) {
                latestChildNeeds.push(
                  `${childDomainFieldLabels[index]} (${fieldValue})`
                );
              }
            });
            toddler.Needs = `

Latest Assessment (${latestAssessmentType}): ${latestChildNeeds.join(", ")}`;
            setLatestChildOptions((o) => [...o, toddler]);
          });
        }

        if (getFieldValue(formResults.ChildAssessmentsNA) !== "true") {
          const childAssessments = JSON.parse(
            getFieldValue(formResults.ChildAssessments)
          );
          const latestAssessmentType = getFieldValue(
            formResults.AssessmentType
          );
          childAssessments.forEach((assessment) => {
            const child = { _id: assessment._id, name: assessment.Name };
            const latestChildNeeds = [];
            childDomainFieldNames.forEach((fieldName, index) => {
              const fieldValue = parseInt(assessment[fieldName]);

              if (fieldValue >= 2) {
                latestChildNeeds.push(
                  `${childDomainFieldLabels[index]} (${fieldValue})`
                );
              }
            });
            child.Needs += `

Latest Assessment (${latestAssessmentType}): ${latestChildNeeds.join(", ")}`;
            setLatestChildOptions((o) => [...o, child]);
          });
        }
      }

      obj.ChildGoals["@value"] = JSON.stringify(toddlers.concat(children));
      return obj;
    });
  };

  const convertFASTAssessments = useCallback(() => {
    setLatestCaregiverOptions([]);
    setLatestChildOptions([]);
    getFamilyNeeds(fastAssessments);
    getCaregiverNeeds(fastAssessments);
    getChildNeeds(fastAssessments);
  }, [fastAssessments]);

  const handleReportMonthChange = ({ currentTarget: input }) => {
    handleChange({ currentTarget: input });

    if (!input.value) {
      return;
    }

    const momentObj = moment();

    if (Number(getFieldValue(data.ReportMonth) > momentObj.month() + 1)) {
      momentObj.subtract(1, "year");
    }

    momentObj.month(data.ReportMonth["@value"] - 1);

    const startOfMonth = momentObj
      .startOf("month")
      .format(moment.HTML5_FMT.DATE);
    const endOfMonth = momentObj.endOf("month").format(moment.HTML5_FMT.DATE);
    setData((d) => {
      const obj = { ...d };
      obj.StartDate["@value"] = startOfMonth;
      obj.EndDate["@value"] = endOfMonth;
      return obj;
    });

    console.log("month change");
    convertEncounters();
  };

  const handleReportDateChange = (e) => {
    handleChange(e);
    console.log("date change");
    convertEncounters();
    convertFASTAssessments();
  };

  const handlePlanDateChange = ({ currentTarget: input }) => {
    handleChange({ currentTarget: input });

    if (!input.value) {
      return;
    }

    setData((d) => {
      const obj = { ...d };
      obj.NextReviewDate["@value"] = moment(input.value)
        .add(30, "days")
        .format(moment.HTML5_FMT.DATE);
      return obj;
    });
  };

  const handleGoalChange = (
    { currentTarget: input },
    fieldIndex,
    fieldName,
    parentIndex
  ) => {
    setData((d) => {
      const obj = { ...d };
      const fieldValue = JSON.parse(getFieldValue(obj[fieldName]));
      fieldValue[fieldIndex].Goals[parentIndex][input.name] = input.value;

      if (!fieldValue[fieldIndex]._id) {
        fieldValue[fieldIndex]._id = cryptoRandomString(10);
      }

      obj[fieldName]["@value"] = JSON.stringify(fieldValue);
      return obj;
    });
  };

  const handleObjectiveChange = (
    { currentTarget: input },
    fieldIndex,
    fieldName,
    firstIndex,
    secondIndex
  ) => {
    setData((d) => {
      const obj = { ...d };
      const fieldValue = JSON.parse(getFieldValue(obj[fieldName]));

      if (fieldName === "FamilyUnitDomainGoals") {
        fieldValue[fieldIndex].Objectives[firstIndex][input.name] = input.value;
      } else {
        fieldValue[fieldIndex].Goals[firstIndex].Objectives[secondIndex][
          input.name
        ] = input.value;
      }

      if (!fieldValue[fieldIndex]._id) {
        fieldValue[fieldIndex]._id = cryptoRandomString(10);
      }

      obj[fieldName]["@value"] = JSON.stringify(fieldValue);
      return obj;
    });
  };

  const handleProgressChange = (
    { currentTarget: input },
    fieldIndex,
    fieldName,
    firstIndex,
    secondIndex,
    thirdIndex
  ) => {
    setData((d) => {
      const obj = { ...d };
      const fieldValue = JSON.parse(getFieldValue(obj[fieldName]));

      if (fieldName === "FamilyUnitDomainGoals") {
        fieldValue[fieldIndex].Objectives[firstIndex].ProgressDetails[
          secondIndex
        ][input.name] = input.value;
      } else {
        fieldValue[fieldIndex].Goals[firstIndex].Objectives[
          secondIndex
        ].ProgressDetails[thirdIndex][input.name] = input.value;
      }

      if (!fieldValue[fieldIndex]._id) {
        fieldValue[fieldIndex]._id = cryptoRandomString(10);
      }

      obj[fieldName]["@value"] = JSON.stringify(fieldValue);
      return obj;
    });
  };

  const handleAddGoal = (fieldName, parentIndex) => {
    setData((d) => {
      const obj = { ...d };
      const fieldValue = JSON.parse(getFieldValue(data[fieldName]));
      fieldValue[parentIndex].Goals.push({
        _id: cryptoRandomString(10),
        Objectives: [{ ProgressDetails: [{}] }],
      });

      if (!fieldValue[parentIndex]._id) {
        fieldValue[parentIndex]._id = cryptoRandomString(10);
      }

      obj[fieldName]["@value"] = JSON.stringify(fieldValue);
      return obj;
    });
  };

  const handleAddObjective = (fieldName, parentIndex, firstIndex) => {
    setData((d) => {
      const obj = { ...d };
      const fieldValue = JSON.parse(getFieldValue(obj[fieldName]));

      if (fieldName === "FamilyUnitDomainGoals") {
        fieldValue[parentIndex].Objectives.push({
          _id: cryptoRandomString(10),
          ProgressDetails: [{}],
        });
      } else {
        fieldValue[parentIndex].Goals[firstIndex].Objectives.push({
          _id: cryptoRandomString(10),
          ProgressDetails: [{}],
        });
      }

      if (!fieldValue[parentIndex]._id) {
        fieldValue[parentIndex]._id = cryptoRandomString(10);
      }

      obj[fieldName]["@value"] = JSON.stringify(fieldValue);
      return obj;
    });
  };

  const handleAddProgressDetail = (
    fieldName,
    parentIndex,
    firstIndex,
    secondIndex
  ) => {
    setData((d) => {
      const obj = { ...d };
      const fieldValue = JSON.parse(getFieldValue(obj[fieldName]));

      if (fieldName === "FamilyUnitDomainGoals") {
        fieldValue[parentIndex].Objectives[firstIndex].ProgressDetails.push({
          _id: cryptoRandomString(10),
          Date,
        });
      } else {
        fieldValue[parentIndex].Goals[firstIndex].Objectives[
          secondIndex
        ].ProgressDetails.push({ _id: cryptoRandomString(10) });
      }

      if (!fieldValue[parentIndex]._id) {
        fieldValue[parentIndex]._id = cryptoRandomString(10);
      }

      obj[fieldName]["@value"] = JSON.stringify(fieldValue);
      return obj;
    });
  };

  const handleLatestChange = (
    { currentTarget: input },
    fieldIndex,
    fieldName,
    options
  ) => {
    if (input.value) {
      setData((d) => {
        const obj = { ...d };
        const fieldValue = JSON.parse(getFieldValue(obj[fieldName]));
        const option = options.filter((o) => o._id === input.value)[0];
        fieldValue[fieldIndex].LatestNeeds = option.Needs;
        obj[fieldName]["@value"] = JSON.stringify(fieldValue);
        return obj;
      });
    }
  };

  const handleDeleteGoal = (fieldName, parentIndex, firstIndex) => {
    setData((d) => {
      const obj = { ...d };
      const fieldValue = JSON.parse(getFieldValue(obj[fieldName]));

      if (fieldName === "FamilyUnitDomainGoals") {
        fieldValue.splice(parentIndex, 1);
      } else {
        fieldValue[parentIndex].Goals.splice(firstIndex, 1);
      }

      obj[fieldName]["@value"] = JSON.stringify(fieldValue);
      return obj;
    });
  };

  const handleDeleteObjective = (
    fieldName,
    parentIndex,
    firstIndex,
    secondIndex
  ) => {
    setData((d) => {
      const obj = { ...d };
      const fieldValue = JSON.parse(getFieldValue(obj[fieldName]));

      if (fieldName === "FamilyUnitDomainGoals") {
        fieldValue[parentIndex].Objectives.splice(firstIndex, 1);
      } else {
        fieldValue[parentIndex].Goals[firstIndex].Objectives.splice(
          secondIndex,
          1
        );
      }

      obj[fieldName]["@value"] = JSON.stringify(fieldValue);
      return obj;
    });
  };

  const handleDeleteProgress = (
    fieldName,
    parentIndex,
    firstIndex,
    secondIndex,
    thirdIndex
  ) => {
    console.log(
      "handleDeleteProgress",
      fieldName,
      parentIndex,
      firstIndex,
      secondIndex,
      thirdIndex
    );
    setData((d) => {
      const obj = { ...d };
      const fieldValue = JSON.parse(getFieldValue(obj[fieldName]));

      if (fieldName === "FamilyUnitDomainGoals") {
        fieldValue[parentIndex].Objectives[firstIndex].ProgressDetails.splice(
          secondIndex,
          1
        );
      } else {
        fieldValue[parentIndex].Goals[firstIndex].Objectives[
          secondIndex
        ].ProgressDetails.splice(thirdIndex, 1);
      }

      obj[fieldName]["@value"] = JSON.stringify(fieldValue);
      return obj;
    });
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        />
        <FormButtonBar
          config={helperConfiguration.buttonBarConfig}
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="h5">{formsyDetails.FormDescription}</div>
              <div className="row">
                <div className="col py-2">
                  {data.ClientInfoLabels["@nameLabel"]}{" "}
                  {formsyDetails.ContactName}
                </div>
                <div className="col py-2">
                  {data.ClientInfoLabels["@divisionLabel"]}{" "}
                  {formsyDetails.ClientProgramName?.split(" - ")[0]}
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <FormSelect
                    name="PlanType"
                    label={data.PlanType["@label"]}
                    error={errors.PlanType}
                    value={getFieldValue(data.PlanType)}
                    options={JSON.parse(
                      data.PlanType["@options"].replaceAll("'", '"')
                    )}
                    onChange={handleChange}
                    identical={compare("PlanType")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <FormInput
                    name="PlanDate"
                    label={data.PlanDate["@label"]}
                    error={errors.PlanDate}
                    value={getFieldValue(data.PlanDate)}
                    onChange={handlePlanDateChange}
                    identical={compare("PlanDate")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    type="date"
                  />
                </div>
              </div>
              <div className="row">
                <div className="col"></div>
                <div className="col">
                  <FormInput
                    name="NextReviewDate"
                    label={data.NextReviewDate["@label"]}
                    error={errors.NextReviewDate}
                    value={getFieldValue(data.NextReviewDate)}
                    onChange={handleChange}
                    identical={compare("NextReviewDate")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    type="date"
                  />
                </div>
              </div>
              <div className="h5">{data.FSPInfoHeader["@label"]}</div>
              <FormInput
                name="StrengthsAndNeeds"
                label={data.StrengthsAndNeeds["@label"]}
                error={errors.StrengthsAndNeeds}
                value={getFieldValue(data.StrengthsAndNeeds)}
                onChange={handleChange}
                identical={compare("StrengthsAndNeeds")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormInput
                name="ServicesAndProviders"
                label={data.ServicesAndProviders["@label"]}
                error={errors.ServicesAndProviders}
                value={getFieldValue(data.ServicesAndProviders)}
                onChange={handleChange}
                identical={compare("ServicesAndProviders")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormInput
                name="PlanParticipants"
                label={data.PlanParticipants["@label"]}
                error={errors.PlanParticipants}
                value={getFieldValue(data.PlanParticipants)}
                onChange={handleChange}
                identical={compare("PlanParticipants")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div>{data.FocusLabel["@label"]}</div>
              <div className="row row-cols-4">
                <div className="col">
                  <FormCheckbox
                    name="FocusAdvocacy"
                    label={data.FocusAdvocacy["@label"]}
                    checked={getFieldValue(data.FocusAdvocacy) === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
                <div className="col">
                  <FormCheckbox
                    name="FocusCaregiverHealth"
                    label={data.FocusCaregiverHealth["@label"]}
                    checked={
                      getFieldValue(data.FocusCaregiverHealth) === "true"
                    }
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
                <div className="col">
                  <FormCheckbox
                    name="FocusEmotionalSupport"
                    label={data.FocusEmotionalSupport["@label"]}
                    checked={
                      getFieldValue(data.FocusEmotionalSupport) === "true"
                    }
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
                <div className="col">
                  <FormCheckbox
                    name="FocusChildHealth"
                    label={data.FocusChildHealth["@label"]}
                    checked={getFieldValue(data.FocusChildHealth) === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
                <div className="col">
                  <FormCheckbox
                    name="FocusGeneralParenting"
                    label={data.FocusGeneralParenting["@label"]}
                    checked={
                      getFieldValue(data.FocusGeneralParenting) === "true"
                    }
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
                <div className="col">
                  <FormCheckbox
                    name="FocusHomemaker"
                    label={data.FocusHomemaker["@label"]}
                    checked={getFieldValue(data.FocusHomemaker) === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
                <div className="col">
                  <FormCheckbox
                    name="FocusCareerExploration"
                    label={data.FocusCareerExploration["@label"]}
                    checked={
                      getFieldValue(data.FocusCareerExploration) === "true"
                    }
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
                <div className="col">
                  <FormCheckbox
                    name="FocusParentEducation"
                    label={data.FocusParentEducation["@label"]}
                    checked={
                      getFieldValue(data.FocusParentEducation) === "true"
                    }
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
                <div className="col">
                  <FormCheckbox
                    name="FocusReferrals"
                    label={data.FocusReferrals["@label"]}
                    checked={getFieldValue(data.FocusReferrals) === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
                <div className="col">
                  <FormCheckbox
                    name="FocusSkillBuilding"
                    label={data.FocusSkillBuilding["@label"]}
                    checked={getFieldValue(data.FocusSkillBuilding) === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
                <div className="col">
                  <FormCheckbox
                    name="FocusChildEducation"
                    label={data.FocusChildEducation["@label"]}
                    checked={getFieldValue(data.FocusChildEducation) === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
                <div className="col">
                  <FormCheckbox
                    name="FocusTransportation"
                    label={data.FocusTransportation["@label"]}
                    checked={getFieldValue(data.FocusTransportation) === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                </div>
                <div className="col">
                  <FormCheckbox
                    name="FocusOther"
                    label={data.FocusOther["@label"]}
                    checked={getFieldValue(data.FocusOther) === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    divClassName="ms-3"
                  />
                  {data.FocusOther["@value"] === "true" ? (
                    <FormInput
                      name="FocusOtherDetails"
                      error={errors.FocusOtherDetails}
                      value={getFieldValue(data.FocusOtherDetails)}
                      onChange={handleChange}
                      identical={compare("FocusOtherDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                </div>
              </div>
              <div className="d-flex">
                <div className="h5">{data.FamilyDomainHeader["@label"]}</div>
                {!formDisabled && fastAssessments.length > 0 ? (
                  data.FamilyUnitNeeds["@value"] ? (
                    <i
                      className="fas fa-sync fa-lg ms-2 mt-3"
                      onClick={() => {
                        if (data.SelectedProgram["@value"]) {
                          convertFASTAssessments(
                            getFieldValue(data.SelectedProgram)
                          );
                        }
                      }}
                    ></i>
                  ) : (
                    <button
                      className="btn btn-info text-white ms-2"
                      onClick={() => {
                        if (data.SelectedProgram["@value"]) {
                          convertFASTAssessments(
                            getFieldValue(data.SelectedProgram)
                          );
                        }
                      }}
                    >
                      Import FAST Assessment Needs
                    </button>
                  )
                ) : null}
              </div>
              {!loadStatus.loadingFastAssessments ? (
                <FormInput
                  name="FamilyUnitNeeds"
                  label={data.FamilyUnitNeeds["@label"]}
                  error={errors.FamilyUnitNeeds}
                  value={getFieldValue(data.FamilyUnitNeeds)}
                  onChange={handleChange}
                  identical={compare("FamilyUnitNeeds")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : (
                <FormInput
                  name="FamilyUnitNeeds"
                  label={data.FamilyUnitNeeds["@label"]}
                  error={errors.FamilyUnitNeeds}
                  value={getFieldValue(data.FamilyUnitNeeds)}
                  onChange={handleChange}
                  identical={compare("FamilyUnitNeeds")}
                  commentsHandler={handleComments}
                  disabled
                />
              )}
              <FieldTable
                name="FamilyUnitDomainGoals"
                commentsHandler={handleComments}
              >
                {data.FamilyUnitDomainGoals["@value"]
                  ? JSON.parse(data.FamilyUnitDomainGoals["@value"]).map(
                      (goal, goalIndex) => (
                        <React.Fragment key={goalIndex}>
                          <FormInput
                            id={`FamilyUnitDomainGoals.${goalIndex}.Goal`}
                            name="Goal"
                            label={
                              <div className="d-flex">
                                {data.FamilyUnitDomainGoals["@goalLabel"]}
                                <i
                                  className="far fa-trash-alt ms-2 d-print-none"
                                  onClick={() => {
                                    handleDeleteGoal(
                                      "FamilyUnitDomainGoals",
                                      goalIndex
                                    );
                                  }}
                                />
                              </div>
                            }
                            value={goal.Goal}
                            onChange={(e) => {
                              handleRowChange(
                                e,
                                goalIndex,
                                "FamilyUnitDomainGoals"
                              );
                            }}
                            disabled={formDisabled}
                          />
                          <div className="ms-5">
                            {goal.Objectives?.map(
                              (objective, objectiveIndex) => (
                                <React.Fragment key={objectiveIndex}>
                                  <FormSelect
                                    id={`FamilyUnitDomainGoals.${goalIndex}.${objectiveIndex}.Objective`}
                                    name="Objective"
                                    label={
                                      <div className="d-flex">
                                        {data.FamilyUnitDomainGoals[
                                          "@objectiveLabel"
                                        ].replace(
                                          "{index}",
                                          objectiveIndex + 1
                                        )}
                                        <i
                                          className="far fa-trash-alt ms-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteObjective(
                                              "FamilyUnitDomainGoals",
                                              goalIndex,
                                              objectiveIndex
                                            );
                                          }}
                                        />
                                      </div>
                                    }
                                    value={objective.Objective}
                                    options={JSON.parse(
                                      data.FamilyUnitDomainGoals[
                                        "@objectiveOptions"
                                      ].replaceAll("'", '"')
                                    ).filter(
                                      (option) =>
                                        objective.Objective === option._id ||
                                        !getFieldValue(
                                          data.FamilyUnitDomainGoals
                                        ).includes(option._id)
                                    )}
                                    onChange={(e) => {
                                      handleObjectiveChange(
                                        e,
                                        goalIndex,
                                        "FamilyUnitDomainGoals",
                                        objectiveIndex
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                  <div className="ms-5">
                                    <FormInput
                                      id={`FamilyUnitDomainGoals.${goalIndex}.${objectiveIndex}.Responsible`}
                                      name="Responsible"
                                      label={
                                        data.FamilyUnitDomainGoals[
                                          "@responsibleLabel"
                                        ]
                                      }
                                      value={objective.Responsible}
                                      onChange={(e) => {
                                        handleObjectiveChange(
                                          e,
                                          goalIndex,
                                          "FamilyUnitDomainGoals",
                                          objectiveIndex
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                    <FormInput
                                      id={`FamilyUnitDomainGoals.${goalIndex}.${objectiveIndex}.Measurement`}
                                      name="Measurement"
                                      label={
                                        data.FamilyUnitDomainGoals[
                                          "@measurementLabel"
                                        ]
                                      }
                                      value={objective.Measurement}
                                      onChange={(e) => {
                                        handleObjectiveChange(
                                          e,
                                          goalIndex,
                                          "FamilyUnitDomainGoals",
                                          objectiveIndex
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                    <FormInput
                                      id={`FamilyUnitDomainGoals.${goalIndex}.${objectiveIndex}.Target`}
                                      name="Target"
                                      label={
                                        data.FamilyUnitDomainGoals[
                                          "@targetLabel"
                                        ]
                                      }
                                      value={objective.Target}
                                      onChange={(e) => {
                                        handleObjectiveChange(
                                          e,
                                          goalIndex,
                                          "FamilyUnitDomainGoals",
                                          objectiveIndex
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                    {getFieldValue(data.PlanType) !==
                                    "Initial" ? (
                                      <>
                                        <div className="mb-2">
                                          {
                                            data.FamilyUnitDomainGoals[
                                              "@progressLabel"
                                            ]
                                          }
                                        </div>
                                        <div className="ms-5">
                                          <div className="container border-start border-top mb-2">
                                            <div className="row border-bottom">
                                              <div className="col border-end py-2">
                                                {
                                                  data.FamilyUnitDomainGoals[
                                                    "@col1"
                                                  ]
                                                }
                                              </div>
                                              <div className="col border-end py-2">
                                                {
                                                  data.FamilyUnitDomainGoals[
                                                    "@col2"
                                                  ]
                                                }
                                              </div>
                                              <div className="col border-end py-2">
                                                {
                                                  data.FamilyUnitDomainGoals[
                                                    "@col3"
                                                  ]
                                                }
                                              </div>
                                              <div className="col border-end py-2">
                                                {
                                                  data.FamilyUnitDomainGoals[
                                                    "@col4"
                                                  ]
                                                }
                                              </div>
                                            </div>
                                            {objective.ProgressDetails?.map(
                                              (progress, progressIndex) => (
                                                <div
                                                  className="row border-bottom"
                                                  key={progressIndex}
                                                >
                                                  <div className="col border-end py-2">
                                                    <div className="d-flex">
                                                      <i
                                                        className="far fa-trash-alt me-2 d-print-none"
                                                        onClick={() => {
                                                          handleDeleteProgress(
                                                            "FamilyUnitDomainGoals",
                                                            goalIndex,
                                                            objectiveIndex,
                                                            progressIndex
                                                          );
                                                        }}
                                                      />
                                                      <FormInput
                                                        id={`FamilyUnitDomainGoals.${goalIndex}.${objectiveIndex}.${progressIndex}.Date`}
                                                        name="Date"
                                                        value={progress.Date}
                                                        onChange={(e) => {
                                                          handleProgressChange(
                                                            e,
                                                            goalIndex,
                                                            "FamilyUnitDomainGoals",
                                                            objectiveIndex,
                                                            progressIndex
                                                          );
                                                        }}
                                                        disabled={formDisabled}
                                                      />
                                                    </div>
                                                  </div>
                                                  <div className="col border-end py-2">
                                                    <FormSelect
                                                      id={`FamilyUnitDomainGoals.${goalIndex}.${objectiveIndex}.${progressIndex}.Progress`}
                                                      name="Progress"
                                                      value={progress.Progress}
                                                      options={JSON.parse(
                                                        data.ProgressOptions[
                                                          "@progressOptions"
                                                        ].replaceAll("'", '"')
                                                      )}
                                                      onChange={(e) => {
                                                        handleProgressChange(
                                                          e,
                                                          goalIndex,
                                                          "FamilyUnitDomainGoals",
                                                          objectiveIndex,
                                                          progressIndex
                                                        );
                                                      }}
                                                      disabled={formDisabled}
                                                    />
                                                  </div>
                                                  <div className="col border-end py-2">
                                                    <FormSelect
                                                      id={`FamilyUnitDomainGoals..${goalIndex}.${objectiveIndex}.${progressIndex}.MIStage`}
                                                      name="MIStage"
                                                      value={progress.MIStage}
                                                      options={JSON.parse(
                                                        data.ProgressOptions[
                                                          "@stageOptions"
                                                        ].replaceAll("'", '"')
                                                      )}
                                                      onChange={(e) => {
                                                        handleProgressChange(
                                                          e,
                                                          goalIndex,
                                                          "FamilyUnitDomainGoals",
                                                          objectiveIndex,
                                                          progressIndex
                                                        );
                                                      }}
                                                      disabled={formDisabled}
                                                    />
                                                  </div>
                                                  <div className="col border-end py-2">
                                                    <FormInput
                                                      id={`FamilyUnitDomainGoals.${goalIndex}.${objectiveIndex}.${progressIndex}.Observations`}
                                                      name="Observations"
                                                      value={
                                                        progress.Observations
                                                      }
                                                      onChange={(e) => {
                                                        handleProgressChange(
                                                          e,
                                                          goalIndex,
                                                          "FamilyUnitDomainGoals",
                                                          objectiveIndex,
                                                          progressIndex
                                                        );
                                                      }}
                                                      disabled={formDisabled}
                                                    />
                                                  </div>
                                                </div>
                                              )
                                            )}
                                          </div>
                                          {!formDisabled ? (
                                            <button
                                              className="btn btn-primary mb-2"
                                              onClick={() => {
                                                handleAddProgressDetail(
                                                  "FamilyUnitDomainGoals",
                                                  goalIndex,
                                                  objectiveIndex
                                                );
                                              }}
                                            >
                                              {
                                                data.FamilyUnitDomainGoals[
                                                  "@addProgressLabel"
                                                ]
                                              }
                                            </button>
                                          ) : null}
                                        </div>
                                      </>
                                    ) : null}
                                  </div>
                                </React.Fragment>
                              )
                            )}
                            {!formDisabled ? (
                              <button
                                className="btn btn-primary mb-2"
                                onClick={() => {
                                  handleAddObjective(
                                    "FamilyUnitDomainGoals",
                                    goalIndex
                                  );
                                }}
                              >
                                {
                                  data.FamilyUnitDomainGoals[
                                    "@addObjectiveLabel"
                                  ]
                                }
                              </button>
                            ) : null}
                          </div>
                        </React.Fragment>
                      )
                    )
                  : null}
                {!formDisabled ? (
                  <button
                    className="btn btn-primary mb-2"
                    onClick={() => {
                      handleAddRow("FamilyUnitDomainGoals", {
                        _id: cryptoRandomString(10),
                        Objectives: [{ ProgressDetails: [{}] }],
                      });
                    }}
                  >
                    {data.FamilyUnitDomainGoals["@addGoalLabel"]}
                  </button>
                ) : null}
                {errors.FamilyUnitDomainGoals !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.FamilyUnitDomainGoals}
                      </div>
                    </div>
                  </div>
                ) : null}
              </FieldTable>
              <div className="h5">{data.CaregiverHeader["@label"]}</div>
              <FieldTable
                name="CaregiverGoals"
                commentsHandler={handleComments}
              >
                {data.CaregiverGoals["@value"]
                  ? JSON.parse(data.CaregiverGoals["@value"]).map(
                      (caregiver, caregiverIndex) => (
                        <div
                          key={caregiverIndex}
                          className="border-bottom border-3 mb-4"
                        >
                          <div className="row">
                            <div className="col">
                              <FormInput
                                id={`CaregiverGoals.${caregiverIndex}.CaregiverName`}
                                name="CaregiverName"
                                label={`${caregiverIndex + 1}. ${
                                  data.CaregiverGoals["@nameLabel"]
                                }`}
                                value={caregiver.CaregiverName}
                                onChange={(e) => {
                                  handleRowChange(
                                    e,
                                    caregiverIndex,
                                    "CaregiverGoals"
                                  );
                                }}
                                disabled={formDisabled}
                                labelClassName="fw-bold"
                              />
                            </div>
                            <div className="col">
                              <FormSelect
                                id={`CaregiverGoals.${caregiverIndex}.LatestCaregiver`}
                                name="LatestCaregiver"
                                label={data.CaregiverGoals["@latestLabel"]}
                                value={caregiver.LatestCaregiver}
                                options={latestCaregiverOptions.filter(
                                  (option) =>
                                    caregiver.LatestCaregiver === option._id ||
                                    !JSON.stringify(
                                      getFieldValue(data.CaregiverGoals)
                                    ).includes(option._id)
                                )}
                                onChange={(e) => {
                                  handleLatestChange(
                                    e,
                                    caregiverIndex,
                                    "CaregiverGoals",
                                    latestCaregiverOptions
                                  );
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <FormInput
                            id={`CaregiverGoals.${caregiverIndex}.Needs`}
                            name="Needs"
                            label={data.CaregiverGoals["@needsLabel"]}
                            value={`${caregiver.Needs ? caregiver.Needs : ""}${
                              caregiver.LatestNeeds
                                ? ` ${caregiver.LatestNeeds}`
                                : ""
                            }`}
                            onChange={(e) => {
                              handleRowChange(
                                e,
                                caregiverIndex,
                                "CaregiverGoals"
                              );
                            }}
                            disabled={formDisabled}
                          />
                          <div className="ms-2">
                            {caregiver.Goals?.map((goal, goalIndex) => (
                              <React.Fragment key={goalIndex}>
                                <FormInput
                                  id={`CaregiverGoals.${caregiverIndex}.${goalIndex}.Goal`}
                                  name="Goal"
                                  label={
                                    <div className="d-flex">
                                      {data.CaregiverGoals["@goalLabel"]}
                                      <i
                                        className="far fa-trash-alt ms-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteGoal(
                                            "CaregiverGoals",
                                            caregiverIndex,
                                            goalIndex
                                          );
                                        }}
                                      />
                                    </div>
                                  }
                                  value={goal.Goal}
                                  onChange={(e) => {
                                    handleGoalChange(
                                      e,
                                      caregiverIndex,
                                      "CaregiverGoals",
                                      goalIndex
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                                <div className="ms-5">
                                  {goal.Objectives?.map(
                                    (objective, objectiveIndex) => (
                                      <React.Fragment key={objectiveIndex}>
                                        <FormSelect
                                          id={`CaregiverGoals.${caregiverIndex}.${goalIndex}.${objectiveIndex}.Objective`}
                                          name="Objective"
                                          label={
                                            <div className="d-flex">
                                              {data.CaregiverGoals[
                                                "@objectiveLabel"
                                              ].replace(
                                                "{index}",
                                                objectiveIndex + 1
                                              )}
                                              <i
                                                className="far fa-trash-alt ms-2 d-print-none"
                                                onClick={() => {
                                                  handleDeleteObjective(
                                                    "CaregiverGoals",
                                                    caregiverIndex,
                                                    goalIndex,
                                                    objectiveIndex
                                                  );
                                                }}
                                              />
                                            </div>
                                          }
                                          value={objective.Objective}
                                          options={JSON.parse(
                                            data.CaregiverGoals[
                                              "@objectiveOptions"
                                            ].replaceAll("'", '"')
                                          ).filter(
                                            (option) =>
                                              objective.Objective ===
                                                option._id ||
                                              !JSON.stringify(
                                                goal.Objectives
                                              ).includes(option._id)
                                          )}
                                          onChange={(e) => {
                                            handleObjectiveChange(
                                              e,
                                              caregiverIndex,
                                              "CaregiverGoals",
                                              goalIndex,
                                              objectiveIndex
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        <div className="ms-5">
                                          <FormInput
                                            id={`CaregiverGoals.${caregiverIndex}.${goalIndex}.${objectiveIndex}.Responsible`}
                                            name="Responsible"
                                            label={
                                              data.CaregiverGoals[
                                                "@responsibleLabel"
                                              ]
                                            }
                                            value={objective.Responsible}
                                            onChange={(e) => {
                                              handleObjectiveChange(
                                                e,
                                                caregiverIndex,
                                                "CaregiverGoals",
                                                goalIndex,
                                                objectiveIndex
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                          <FormInput
                                            id={`CaregiverGoals.${caregiverIndex}.${goalIndex}.${objectiveIndex}.Measurement`}
                                            name="Measurement"
                                            label={
                                              data.CaregiverGoals[
                                                "@measurementLabel"
                                              ]
                                            }
                                            value={objective.Measurement}
                                            onChange={(e) => {
                                              handleObjectiveChange(
                                                e,
                                                caregiverIndex,
                                                "CaregiverGoals",
                                                goalIndex,
                                                objectiveIndex
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                          <FormInput
                                            id={`CaregiverGoals.${caregiverIndex}.${goalIndex}.${objectiveIndex}.Target`}
                                            name="Target"
                                            label={
                                              data.CaregiverGoals[
                                                "@targetLabel"
                                              ]
                                            }
                                            value={objective.Target}
                                            onChange={(e) => {
                                              handleObjectiveChange(
                                                e,
                                                caregiverIndex,
                                                "CaregiverGoals",
                                                goalIndex,
                                                objectiveIndex
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                          {getFieldValue(data.PlanType) !==
                                          "Initial" ? (
                                            <>
                                              <div className="mb-2">
                                                {
                                                  data.CaregiverGoals[
                                                    "@progressLabel"
                                                  ]
                                                }
                                              </div>
                                              <div className="ms-5">
                                                <div className="container border-start border-top mb-2">
                                                  <div className="row border-bottom">
                                                    <div className="col border-end py-2">
                                                      {
                                                        data.CaregiverGoals[
                                                          "@col1"
                                                        ]
                                                      }
                                                    </div>
                                                    <div className="col border-end py-2">
                                                      {
                                                        data.CaregiverGoals[
                                                          "@col2"
                                                        ]
                                                      }
                                                    </div>
                                                    <div className="col border-end py-2">
                                                      {
                                                        data.CaregiverGoals[
                                                          "@col3"
                                                        ]
                                                      }
                                                    </div>
                                                    <div className="col border-end py-2">
                                                      {
                                                        data.CaregiverGoals[
                                                          "@col4"
                                                        ]
                                                      }
                                                    </div>
                                                  </div>
                                                  {objective.ProgressDetails?.map(
                                                    (
                                                      progress,
                                                      progressIndex
                                                    ) => (
                                                      <div
                                                        className="row border-bottom"
                                                        key={progressIndex}
                                                      >
                                                        <div className="col border-end py-2">
                                                          <div className="d-flex">
                                                            <i
                                                              className="far fa-trash-alt me-2 d-print-none"
                                                              onClick={() => {
                                                                handleDeleteProgress(
                                                                  "CaregiverGoals",
                                                                  caregiverIndex,
                                                                  goalIndex,
                                                                  objectiveIndex,
                                                                  progressIndex
                                                                );
                                                              }}
                                                            />
                                                            <FormInput
                                                              id={`CaregiverGoals.${caregiverIndex}.${goalIndex}.${objectiveIndex}.${progressIndex}.Date`}
                                                              name="Date"
                                                              value={
                                                                progress.Date
                                                              }
                                                              onChange={(e) => {
                                                                handleProgressChange(
                                                                  e,
                                                                  caregiverIndex,
                                                                  "CaregiverGoals",
                                                                  goalIndex,
                                                                  objectiveIndex,
                                                                  progressIndex
                                                                );
                                                              }}
                                                              disabled={
                                                                formDisabled
                                                              }
                                                            />
                                                          </div>
                                                        </div>
                                                        <div className="col border-end py-2">
                                                          <FormSelect
                                                            id={`CaregiverGoals.${caregiverIndex}.${goalIndex}.${objectiveIndex}.${progressIndex}.Progress`}
                                                            name="Progress"
                                                            value={
                                                              progress.Progress
                                                            }
                                                            options={JSON.parse(
                                                              data.ProgressOptions[
                                                                "@progressOptions"
                                                              ].replaceAll(
                                                                "'",
                                                                '"'
                                                              )
                                                            )}
                                                            onChange={(e) => {
                                                              handleProgressChange(
                                                                e,
                                                                caregiverIndex,
                                                                "CaregiverGoals",
                                                                goalIndex,
                                                                objectiveIndex,
                                                                progressIndex
                                                              );
                                                            }}
                                                            disabled={
                                                              formDisabled
                                                            }
                                                          />
                                                        </div>
                                                        <div className="col border-end py-2">
                                                          <FormSelect
                                                            id={`CaregiverGoals.${caregiverIndex}.${goalIndex}.${objectiveIndex}.${progressIndex}.MIStage`}
                                                            name="MIStage"
                                                            value={
                                                              progress.MIStage
                                                            }
                                                            options={JSON.parse(
                                                              data.ProgressOptions[
                                                                "@stageOptions"
                                                              ].replaceAll(
                                                                "'",
                                                                '"'
                                                              )
                                                            )}
                                                            onChange={(e) => {
                                                              handleProgressChange(
                                                                e,
                                                                caregiverIndex,
                                                                "CaregiverGoals",
                                                                goalIndex,
                                                                objectiveIndex,
                                                                progressIndex
                                                              );
                                                            }}
                                                            disabled={
                                                              formDisabled
                                                            }
                                                          />
                                                        </div>
                                                        <div className="col border-end py-2">
                                                          <FormInput
                                                            id={`CaregiverGoals.${caregiverIndex}.${goalIndex}.${objectiveIndex}.${progressIndex}.Observations`}
                                                            name="Observations"
                                                            value={
                                                              progress.Observations
                                                            }
                                                            onChange={(e) => {
                                                              handleProgressChange(
                                                                e,
                                                                caregiverIndex,
                                                                "CaregiverGoals",
                                                                goalIndex,
                                                                objectiveIndex,
                                                                progressIndex
                                                              );
                                                            }}
                                                            disabled={
                                                              formDisabled
                                                            }
                                                          />
                                                        </div>
                                                      </div>
                                                    )
                                                  )}
                                                </div>
                                                {!formDisabled ? (
                                                  <button
                                                    className="btn btn-primary mb-2"
                                                    onClick={() => {
                                                      handleAddProgressDetail(
                                                        "CaregiverGoals",
                                                        caregiverIndex,
                                                        goalIndex,
                                                        objectiveIndex
                                                      );
                                                    }}
                                                  >
                                                    {
                                                      data.CaregiverGoals[
                                                        "@addProgressLabel"
                                                      ]
                                                    }
                                                  </button>
                                                ) : null}
                                              </div>
                                            </>
                                          ) : null}
                                        </div>
                                      </React.Fragment>
                                    )
                                  )}
                                  {!formDisabled ? (
                                    <button
                                      className="btn btn-primary mb-2"
                                      onClick={() => {
                                        handleAddObjective(
                                          "CaregiverGoals",
                                          caregiverIndex,
                                          goalIndex
                                        );
                                      }}
                                    >
                                      {
                                        data.CaregiverGoals[
                                          "@addObjectiveLabel"
                                        ]
                                      }
                                    </button>
                                  ) : null}
                                </div>
                              </React.Fragment>
                            ))}
                            {!formDisabled ? (
                              <button
                                className="btn btn-primary mb-2"
                                onClick={() => {
                                  handleAddGoal(
                                    "CaregiverGoals",
                                    caregiverIndex
                                  );
                                }}
                              >
                                {data.CaregiverGoals["@addGoalLabel"]}
                              </button>
                            ) : null}
                          </div>
                        </div>
                      )
                    )
                  : null}
                {!formDisabled ? (
                  <>
                    <button
                      className="btn btn-primary mb-2"
                      onClick={() => {
                        handleAddRow("CaregiverGoals", {
                          _id: cryptoRandomString(10),
                          Goals: [
                            {
                              Objectives: [{ ProgressDetails: [{}] }],
                            },
                          ],
                        });
                      }}
                    >
                      {data.CaregiverGoals["@addCaregiverLabel"]}
                    </button>
                    <br />
                  </>
                ) : null}
                {errors.CaregiverGoals !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.CaregiverGoals}
                      </div>
                    </div>
                  </div>
                ) : null}
              </FieldTable>
              <div className="h5">{data.ChildHeader["@label"]}</div>
              <FieldTable name="ChildGoals" commentsHandler={handleComments}>
                {data.ChildGoals["@value"]
                  ? JSON.parse(data.ChildGoals["@value"]).map(
                      (child, childIndex) => (
                        <div
                          key={childIndex}
                          className="border-bottom border-3 mb-4"
                        >
                          <div className="row">
                            <div className="col">
                              <FormInput
                                id={`ChildGoals.${childIndex}.ChildName`}
                                name="ChildName"
                                label={
                                  <div className="d-flex">
                                    {`${childIndex + 1}. ${
                                      data.ChildGoals["@nameLabel"]
                                    }`}
                                    <i
                                      className="far fa-trash-alt ms-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          "ChildGoals",
                                          childIndex
                                        );
                                      }}
                                    />
                                  </div>
                                }
                                value={child.ChildName}
                                onChange={(e) => {
                                  handleRowChange(e, childIndex, "ChildGoals");
                                }}
                                disabled={formDisabled}
                                labelClassName="fw-bold"
                              />
                            </div>
                            <div className="col">
                              <FormSelect
                                id={`ChildGoals.${childIndex}.LatestChild`}
                                name="LatestChild"
                                label={data.ChildGoals["@latestLabel"]}
                                value={child.LatestChild}
                                options={latestChildOptions.filter(
                                  (option) =>
                                    child.LatestChild === option._id ||
                                    !JSON.stringify(
                                      getFieldValue(data.ChildGoals)
                                    ).includes(option._id)
                                )}
                                onChange={(e) => {
                                  handleLatestChange(
                                    e,
                                    childIndex,
                                    "ChildGoals",
                                    latestChildOptions
                                  );
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <FormInput
                            id={`ChildGoals.${childIndex}.Needs`}
                            name="Needs"
                            label={data.ChildGoals["@needsLabel"]}
                            value={`${child.Needs ? child.Needs : ""}${
                              child.LatestNeeds ? ` ${child.LatestNeeds}` : ""
                            }`}
                            onChange={(e) => {
                              handleRowChange(e, childIndex, "ChildGoals");
                            }}
                            disabled={formDisabled}
                          />
                          <div className="ms-2">
                            {child.Goals?.map((goal, goalIndex) => (
                              <React.Fragment key={goalIndex}>
                                <FormInput
                                  id={`ChildGoals.${childIndex}.${goalIndex}.Goal`}
                                  name="Goal"
                                  label={
                                    <div className="d-flex">
                                      {data.ChildGoals["@goalLabel"]}
                                      <i
                                        className="far fa-trash-alt ms-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteGoal(
                                            "ChildGoals",
                                            childIndex,
                                            goalIndex
                                          );
                                        }}
                                      />
                                    </div>
                                  }
                                  value={goal.Goal}
                                  onChange={(e) => {
                                    handleGoalChange(
                                      e,
                                      childIndex,
                                      "ChildGoals",
                                      goalIndex
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                                <div className="ms-5">
                                  {goal.Objectives?.map(
                                    (objective, objectiveIndex) => (
                                      <React.Fragment key={objectiveIndex}>
                                        <FormSelect
                                          id={`ChildGoals.${childIndex}.${goalIndex}.${objectiveIndex}.Objective`}
                                          name="Objective"
                                          label={
                                            <div className="d-flex">
                                              {data.ChildGoals[
                                                "@objectiveLabel"
                                              ].replace(
                                                "{index}",
                                                objectiveIndex + 1
                                              )}
                                              <i
                                                className="far fa-trash-alt ms-2 d-print-none"
                                                onClick={() => {
                                                  handleDeleteObjective(
                                                    "ChildGoals",
                                                    childIndex,
                                                    goalIndex,
                                                    objectiveIndex
                                                  );
                                                }}
                                              />
                                            </div>
                                          }
                                          value={objective.Objective}
                                          options={JSON.parse(
                                            data.ChildGoals[
                                              "@objectiveOptions"
                                            ].replaceAll("'", '"')
                                          ).filter(
                                            (option) =>
                                              objective.Objective ===
                                                option._id ||
                                              !JSON.stringify(
                                                goal.Objectives
                                              ).includes(option._id)
                                          )}
                                          onChange={(e) => {
                                            handleObjectiveChange(
                                              e,
                                              childIndex,
                                              "ChildGoals",
                                              goalIndex,
                                              objectiveIndex
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        <div className="ms-5">
                                          <FormInput
                                            id={`ChildGoals.${childIndex}.${goalIndex}.${objectiveIndex}.Responsible`}
                                            name="Responsible"
                                            label={
                                              data.ChildGoals[
                                                "@responsibleLabel"
                                              ]
                                            }
                                            value={objective.Responsible}
                                            onChange={(e) => {
                                              handleObjectiveChange(
                                                e,
                                                childIndex,
                                                "ChildGoals",
                                                goalIndex,
                                                objectiveIndex
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                          <FormInput
                                            id={`ChildGoals.${childIndex}.${goalIndex}.${objectiveIndex}.Measurement`}
                                            name="Measurement"
                                            label={
                                              data.ChildGoals[
                                                "@measurementLabel"
                                              ]
                                            }
                                            value={objective.Measurement}
                                            onChange={(e) => {
                                              handleObjectiveChange(
                                                e,
                                                childIndex,
                                                "ChildGoals",
                                                goalIndex,
                                                objectiveIndex
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                          <FormInput
                                            id={`ChildGoals.${childIndex}.${goalIndex}.${objectiveIndex}.Target`}
                                            name="Target"
                                            label={
                                              data.ChildGoals["@targetLabel"]
                                            }
                                            value={objective.Target}
                                            onChange={(e) => {
                                              handleObjectiveChange(
                                                e,
                                                childIndex,
                                                "ChildGoals",
                                                goalIndex,
                                                objectiveIndex
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                          {getFieldValue(data.PlanType) !==
                                          "Initial" ? (
                                            <>
                                              <div className="mb-2 ">
                                                {
                                                  data.ChildGoals[
                                                    "@progressLabel"
                                                  ]
                                                }
                                              </div>
                                              <div className="ms-5">
                                                <div className="container border-start border-top mb-2">
                                                  <div className="row border-bottom">
                                                    <div className="col border-end py-2">
                                                      {data.ChildGoals["@col1"]}
                                                    </div>
                                                    <div className="col border-end py-2">
                                                      {data.ChildGoals["@col2"]}
                                                    </div>
                                                    <div className="col border-end py-2">
                                                      {data.ChildGoals["@col3"]}
                                                    </div>
                                                    <div className="col border-end py-2">
                                                      {data.ChildGoals["@col4"]}
                                                    </div>
                                                  </div>
                                                  {objective.ProgressDetails?.map(
                                                    (
                                                      progress,
                                                      progressIndex
                                                    ) => (
                                                      <div
                                                        className="row border-bottom"
                                                        key={progressIndex}
                                                      >
                                                        <div className="col border-end py-2">
                                                          <div className="d-flex">
                                                            <i
                                                              className="far fa-trash-alt me-2 d-print-none"
                                                              onClick={() => {
                                                                handleDeleteProgress(
                                                                  "ChildGoals",
                                                                  childIndex,
                                                                  goalIndex,
                                                                  objectiveIndex,
                                                                  progressIndex
                                                                );
                                                              }}
                                                            />
                                                            <FormInput
                                                              id={`ChildGoals.${childIndex}.${goalIndex}.${objectiveIndex}.${progressIndex}.Date`}
                                                              name="Date"
                                                              value={
                                                                progress.Date
                                                              }
                                                              onChange={(e) => {
                                                                handleProgressChange(
                                                                  e,
                                                                  childIndex,
                                                                  "ChildGoals",
                                                                  goalIndex,
                                                                  objectiveIndex,
                                                                  progressIndex
                                                                );
                                                              }}
                                                              disabled={
                                                                formDisabled
                                                              }
                                                            />
                                                          </div>
                                                        </div>
                                                        <div className="col border-end py-2">
                                                          <FormSelect
                                                            id={`ChildGoals.${childIndex}.${goalIndex}.${objectiveIndex}.${progressIndex}.Progress`}
                                                            name="Progress"
                                                            value={
                                                              progress.Progress
                                                            }
                                                            options={JSON.parse(
                                                              data.ProgressOptions[
                                                                "@progressOptions"
                                                              ].replaceAll(
                                                                "'",
                                                                '"'
                                                              )
                                                            )}
                                                            onChange={(e) => {
                                                              handleProgressChange(
                                                                e,
                                                                childIndex,
                                                                "ChildGoals",
                                                                goalIndex,
                                                                objectiveIndex,
                                                                progressIndex
                                                              );
                                                            }}
                                                            disabled={
                                                              formDisabled
                                                            }
                                                          />
                                                        </div>
                                                        <div className="col border-end py-2">
                                                          <FormSelect
                                                            id={`ChildGoals.${childIndex}.${goalIndex}.${objectiveIndex}.${progressIndex}.MIStage`}
                                                            name="MIStage"
                                                            value={
                                                              progress.MIStage
                                                            }
                                                            options={JSON.parse(
                                                              data.ProgressOptions[
                                                                "@stageOptions"
                                                              ].replaceAll(
                                                                "'",
                                                                '"'
                                                              )
                                                            )}
                                                            onChange={(e) => {
                                                              handleProgressChange(
                                                                e,
                                                                childIndex,
                                                                "ChildGoals",
                                                                goalIndex,
                                                                objectiveIndex,
                                                                progressIndex
                                                              );
                                                            }}
                                                            disabled={
                                                              formDisabled
                                                            }
                                                          />
                                                        </div>
                                                        <div className="col border-end py-2">
                                                          <FormInput
                                                            id={`ChildGoals.${childIndex}.${goalIndex}.${objectiveIndex}.${progressIndex}.Observations`}
                                                            name="Observations"
                                                            value={
                                                              progress.Observations
                                                            }
                                                            onChange={(e) => {
                                                              handleProgressChange(
                                                                e,
                                                                childIndex,
                                                                "ChildGoals",
                                                                goalIndex,
                                                                objectiveIndex,
                                                                progressIndex
                                                              );
                                                            }}
                                                            disabled={
                                                              formDisabled
                                                            }
                                                          />
                                                        </div>
                                                      </div>
                                                    )
                                                  )}
                                                </div>
                                                {!formDisabled ? (
                                                  <button
                                                    className="btn btn-primary mb-2"
                                                    onClick={() => {
                                                      handleAddProgressDetail(
                                                        "ChildGoals",
                                                        childIndex,
                                                        goalIndex,
                                                        objectiveIndex
                                                      );
                                                    }}
                                                  >
                                                    {
                                                      data.ChildGoals[
                                                        "@addProgressLabel"
                                                      ]
                                                    }
                                                  </button>
                                                ) : null}
                                              </div>
                                            </>
                                          ) : null}
                                        </div>
                                      </React.Fragment>
                                    )
                                  )}
                                  {!formDisabled ? (
                                    <button
                                      className="btn btn-primary mb-2"
                                      onClick={() => {
                                        handleAddObjective(
                                          "ChildGoals",
                                          childIndex,
                                          goalIndex
                                        );
                                      }}
                                    >
                                      {data.ChildGoals["@addObjectiveLabel"]}
                                    </button>
                                  ) : null}
                                </div>
                              </React.Fragment>
                            ))}
                            {!formDisabled ? (
                              <>
                                <button
                                  className="btn btn-primary mb-2"
                                  onClick={() => {
                                    handleAddGoal("ChildGoals", childIndex);
                                  }}
                                >
                                  {data.ChildGoals["@addGoalLabel"]}
                                </button>
                                <br />
                              </>
                            ) : null}
                          </div>
                        </div>
                      )
                    )
                  : null}
                {!formDisabled ? (
                  <>
                    <button
                      className="btn btn-primary mb-2"
                      onClick={() => {
                        handleAddRow("ChildGoals", {
                          _id: cryptoRandomString(10),
                          Goals: [
                            {
                              Objectives: [{ ProgressDetails: [{}] }],
                            },
                          ],
                        });
                      }}
                    >
                      {data.ChildGoals["@addChildLabel"]}
                    </button>
                    <br />
                  </>
                ) : null}
                {errors.ChildGoals !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.ChildGoals}
                      </div>
                    </div>
                  </div>
                ) : null}
              </FieldTable>
              <div className="h5">{data.ContactHeader["@label"]}</div>
              <div className="row">
                <div className="col-6">
                  <FormSelect
                    name="ReportMonth"
                    label="Report Month:"
                    error={errors.ReportMonth}
                    value={data.ReportMonth["@value"]}
                    options={generalOptionSets.month}
                    onChange={handleReportMonthChange}
                    identical={compare("ReportMonth")}
                    commentsHandler={handleComments}
                    disabled={formDisabled || !loadStatus.encountersLoaded}
                    divClassName="w-50"
                  />
                </div>
                <div className="col">
                  <FormInput
                    name="StartDate"
                    label={data.StartDate["@label"]}
                    error={errors.StartDate}
                    value={getFieldValue(data.StartDate)}
                    onChange={handleReportDateChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled || !loadStatus.encountersLoaded}
                    type="date"
                  />
                </div>
                <div className="col">
                  <FormInput
                    name="EndDate"
                    label={data.EndDate["@label"]}
                    error={errors.EndDate}
                    value={getFieldValue(data.EndDate)}
                    onChange={handleReportDateChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled || !loadStatus.encountersLoaded}
                    type="date"
                  />
                </div>
              </div>
              <FieldTable
                name="ContactDetails"
                commentsHandler={handleComments}
              >
                {data.ContactDetails["@label"]}
                <div className="container border-start border-top mb-2">
                  <div className="row border-bottom">
                    <div className="col border-end py-2">
                      {data.ContactDetails["@col1"]}
                    </div>
                    <div className="col border-end py-2">
                      {data.ContactDetails["@col2"]}
                    </div>
                  </div>
                  {JSON.parse(getFieldValue(data.ContactDetails) || "[]").map(
                    (item, index) => (
                      <div key={item._id} className="row border-bottom">
                        <div className="col py-2 border-end">
                          <div className="d-flex">
                            {!formDisabled ? (
                              <i
                                className="far fa-trash-alt me-2 mt-2 d-print-none"
                                onClick={() => {
                                  handleDeleteRow(index, "ContactDetails");
                                }}
                              />
                            ) : null}
                            <FormInput
                              id={`ContactDetails.VisitDate.${index}`}
                              name="VisitDate"
                              value={item.VisitDate}
                              onChange={(e) => {
                                handleRowChange(e, index, "ContactDetails");
                              }}
                              disabled={formDisabled}
                              type="date"
                            />
                          </div>
                        </div>
                        <div className="col py-2 border-end">
                          <FormInput
                            id={`ContactDetails.VisitDuration.${index}`}
                            name="VisitDuration"
                            value={item.VisitDuration}
                            onChange={(e) => {
                              handleRowChange(e, index, "ContactDetails");
                            }}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    )
                  )}
                </div>
                {!formDisabled ? (
                  <>
                    <button
                      className="btn btn-primary mb-2"
                      onClick={() => {
                        handleAddRow("ContactDetails", {});
                      }}
                    >
                      {data.ContactDetails["@addContactLabel"]}
                    </button>
                    <br />
                  </>
                ) : null}
                {errors.ContactDetails !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.ContactDetails}
                      </div>
                    </div>
                  </div>
                ) : null}
              </FieldTable>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={helperConfiguration.finalSignType}
              initialSignatureType={helperConfiguration.signatureType}
            />
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default SCFRCPSFamilyServicePlan;
